import React, { useState, Fragment } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { LoadingSpinner } from 'legacy/packages/ft-cb';
import Message from 'legacy-src/utility/Message';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import Button from 'chameleon/ui-stack/ui/base/button';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

import Icon from 'chameleon/ui-stack/ui/base/icon';

import { tabs, validationActionTypes } from './constants';
import EditScoringContainer from './EditItem/EditScoring/EditScoringContainer';

import LearnosityQuestionEditor from './LearnosityQuestionEditor';
import {
  AUDIO_WIDGET_JSON,
  AUDIO_WIDGET_TEMPLATE,
  FILE_UPLOAD_WIDGET_JSON,
  FILE_UPLOAD_WIDGET_TEMPLATE,
  FOREIGN_LANGUAGE_FORMATTING_OPTIONS,
  FRENCH_CHARACTER_MAPS,
  FRQ_WIDGET_JSON,
  FRQ_WIDGET_TEMPLATE,
  GERMAN_CHARACTER_MAPS,
  ITALIAN_CHARACTER_MAPS,
  SPANISH_CHARACTER_MAPS,
  MATHEMATICS_FORMATTING_OPTIONS,
  COMPUTER_SCIENCE_FORMATTING_OPTIONS,
} from '../../learnosity/utils/constants';
import { setWidgetFields } from '../../learnosity/utils/helpers';
import LearnosityFrqTemplate from './LearnosityFrqTemplate';
import TagsEditor from './TagsEditor';
import FrqSingleQuestionPreview from './EditItem/FrqSingleQuestionPreview';
import {
  AP_FRENCH,
  AP_GERMAN,
  AP_ITALIAN,
  AP_SPANISH,
  AP_SPANISH_LIT,
  AP_CALCULUS_AB,
  AP_CALCULUS_BC,
  AP_STATISTIC,
  AP_CHEMISTRY,
  AP_BIOLOGY,
  AP_PHYSICS_1,
  AP_PHYSICS_2,
  AP_PHYSICS_C_MECHANIC,
  AP_PHYSICS_C_EM,
  AP_CSA,
  AP_PRE_CALCULUS,
} from 'chameleon-src/constants/subjects';

const getWidgetTypes = (masterSubjectId) => {
  let widget = { ...FRQ_WIDGET_JSON };
  switch (masterSubjectId) {
    case AP_FRENCH:
      widget.formatting_options = FOREIGN_LANGUAGE_FORMATTING_OPTIONS;
      widget.character_map = FRENCH_CHARACTER_MAPS;
      break;
    case AP_GERMAN:
      widget.formatting_options = FOREIGN_LANGUAGE_FORMATTING_OPTIONS;
      widget.character_map = GERMAN_CHARACTER_MAPS;
      break;
    case AP_ITALIAN:
      widget.formatting_options = FOREIGN_LANGUAGE_FORMATTING_OPTIONS;
      widget.character_map = ITALIAN_CHARACTER_MAPS;
      break;
    case AP_SPANISH:
    case AP_SPANISH_LIT:
      widget.formatting_options = FOREIGN_LANGUAGE_FORMATTING_OPTIONS;
      widget.character_map = SPANISH_CHARACTER_MAPS;
      break;
    case AP_CALCULUS_AB:
    case AP_CALCULUS_BC:
    case AP_STATISTIC:
    case AP_CHEMISTRY:
    case AP_BIOLOGY:
    case AP_PHYSICS_1:
    case AP_PHYSICS_2:
    case AP_PHYSICS_C_MECHANIC:
    case AP_PHYSICS_C_EM:
    case AP_PRE_CALCULUS:
      widget.formatting_options = MATHEMATICS_FORMATTING_OPTIONS;
      widget.characterMapCharacters = [];
      break;
    case AP_CSA:
      widget.formatting_options = COMPUTER_SCIENCE_FORMATTING_OPTIONS;
      break;
    default:
    // No changes for most courses
  }
  return [
    {
      type: 'longtextV2',
      header: 'Essay',
      image: 'doc',
      json: widget,
      template: FRQ_WIDGET_TEMPLATE,
    },
    {
      type: 'audio',
      header: 'Audio',
      image: 'mic',
      json: AUDIO_WIDGET_JSON,
      template: AUDIO_WIDGET_TEMPLATE,
    },
    {
      type: 'fileupload',
      header: 'File Upload',
      image: 'upload',
      json: FILE_UPLOAD_WIDGET_JSON,
      template: FILE_UPLOAD_WIDGET_TEMPLATE,
    },
  ];
};

const FrqEditor = ({
  isEdit,
  isEditingItem,
  initOptions,
  isLoading,
  onAuthoringApiInstanceReady = () => {},
  onFinallyReady = () => {},
  authoringApiInstance,
  refetchInitOptions = () => {},
  tags,
  subjectInfo,
  updateTags,
  showValidation,
  selectedTab,
  learnosityItemReference,
  dispatchValidation,
  validations,
  flattenHierarchy,
  twoLevelQBFilterFF,
}) => {
  const helpContent = `Add a way for your students to respond to this question by choosing
  one of the available student response types below.`;

  const widgetTypes = getWidgetTypes(parseInt(subjectInfo.masterSubjectId, 10));

  const [currentWidgetTypeIndex, setCurrentWidgetTypeIndex] = useState(null);

  const [isEditInitialLoading, setEditInitialLoading] = useState(isEditingItem);
  const [widgetJson, setWidgetJson] = useState({});
  const [isInlinePreview, setIsInlinePreview] = useState(false);

  const addQuestion = () => {
    const json = authoringApiInstance.getWidget();
    if (json.stimulus && json.stimulus.trim() && json.stimulus !== '&nbsp;') {
      setIsInlinePreview(true);
      dispatchValidation({
        type: validationActionTypes.hasQuestion,
        payload: true,
      });
      setWidgetJson(json);
    }
  };

  const singlePreviewActions = (className) =>
    authoringApiInstance && (
      <div className={`lrn-question-toolbar flex ${className}`}>
        <Button
          isDangerOutline
          iconProps={{
            width: '14px',
          }}
          className="mr-4"
          onClick={() => {
            if (isEmpty(widgetJson)) {
              onAuthoringApiInstanceReady(null);
              refetchInitOptions();
              return setCurrentWidgetTypeIndex(null);
            }

            setWidgetFields(authoringApiInstance.editorApp(), {
              stimulus: widgetJson.stimulus,
            });
            setIsInlinePreview(true);
          }}
        >
          <span className="pr-4">&times;</span>Cancel
        </Button>
        <ButtonWithIcon
          icon="save"
          isPrimary
          onClick={() => addQuestion(authoringApiInstance)}
        >
          Save
        </ButtonWithIcon>
      </div>
    );

  const previewTab = selectedTab === tabs.preview;
  const scoringTab = selectedTab === tabs.scoring;
  const editTab = selectedTab === tabs.edit;

  const showTypeChooser =
    !isEditInitialLoading && editTab && currentWidgetTypeIndex === null;
  const showTagsSelector = isInlinePreview || showTypeChooser;

  const handleRemoveSingleQuestion = () => {
    // eslint-disable-next-line no-alert
    if (
      window.confirm(`Are you sure you want to delete this response?
      This action can not be undone`)
    ) {
      dispatchValidation({
        type: validationActionTypes.hasQuestion,
        payload: false,
      });
      setIsInlinePreview(false);
      setCurrentWidgetTypeIndex(null);
      setWidgetJson(null);
    }
  };

  const handleEditSingleQuestion = () => {
    setIsInlinePreview(false);
  };

  const showProvider = (!isInlinePreview && editTab) || previewTab;
  const showButtons = !previewTab && currentWidgetTypeIndex !== null;

  return (
    <Fragment>
      <div>
        {showTypeChooser && (
          <div className="question-types">
            <Message>{helpContent}</Message>

            {showValidation && !validations.hasQuestion && (
              <Message className="--error">
                In order to publish a question, you must include one of the{' '}
                <strong>Student Response</strong> types below.
              </Message>
            )}

            <div className="row my-10">
              {widgetTypes.map((type, index) => (
                <div
                  className="col3 border border-gray-300 rounded-2xl p-10"
                  key={type.type}
                >
                  <h3 className="mt-0 mb-10 text-center">
                    <Icon name={type.image} className="align-middle" />
                    <span className="ml-4">{type.header} Question</span>
                  </h3>
                  <Button
                    className="w-full"
                    isPrimary
                    onClick={() => {
                      setIsInlinePreview(false);
                      setCurrentWidgetTypeIndex(index);
                    }}
                  >
                    <span>Author {type.header} Question</span>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {isInlinePreview && editTab && currentWidgetTypeIndex !== null && (
          <FrqSingleQuestionPreview
            remove={handleRemoveSingleQuestion}
            edit={handleEditSingleQuestion}
            header={`${widgetTypes[currentWidgetTypeIndex].header} Question`}
            stimulus={widgetJson.stimulus}
          />
        )}

        <div className={cn('question', { '--hidden': !showProvider })}>
          {showButtons && (
            <Fragment>
              <LearnosityFrqTemplate
                questionTypeHeading={widgetTypes[currentWidgetTypeIndex].header}
              />
              <Message className="mb-20">
                Include a stem and any instructions for your students.
              </Message>
              {singlePreviewActions('--shifted')}
            </Fragment>
          )}

          {currentWidgetTypeIndex !== null && (
            <LearnosityQuestionEditor
              shouldSetWidgets
              widgetJson={{
                ...widgetTypes[currentWidgetTypeIndex].json,
                ...widgetJson,
              }}
              widgetTemplate={widgetTypes[currentWidgetTypeIndex].template}
              initOptions={initOptions}
              isLoading={isLoading}
              onceWidgetReady={isEditInitialLoading && addQuestion}
              onReady={onAuthoringApiInstanceReady}
            />
          )}

          {showButtons && singlePreviewActions()}

          {isEditInitialLoading && <LoadingSpinner />}

          {isEditingItem && (
            <div className="--offset">
              {/* we have custom layout in initOptions make sure we always provide one when loading Learnosity*/}
              <LearnosityFrqTemplate questionTypeHeading="" />
              <LearnosityQuestionEditor
                initOptions={initOptions}
                onceWidgetReady={() => {
                  // set already created data to question
                  const index = widgetTypes.findIndex(
                    (widget) =>
                      widget.type === authoringApiInstance.getWidget().type,
                  );

                  // autoselect widget type
                  setCurrentWidgetTypeIndex(index);

                  addQuestion(authoringApiInstance);
                  setEditInitialLoading(false);
                  onFinallyReady();
                }}
                onReady={onAuthoringApiInstanceReady}
              />
            </div>
          )}
        </div>

        {showTagsSelector && (
          <TagsEditor
            isEdit={isEdit}
            subjectInfo={subjectInfo}
            tags={tags}
            updateTags={updateTags}
            showValidation={showValidation}
            validations={validations}
            questionType="FRQ"
            flattenHierarchy={flattenHierarchy}
            twoLevelQBFilterFF={twoLevelQBFilterFF}
          />
        )}
      </div>

      <div hidden={!scoringTab}>
        <EditScoringContainer
          isEditingItem={isEditingItem}
          learnosityItemReference={learnosityItemReference}
          dispatchValidation={dispatchValidation}
          showValidation={showValidation}
          validations={validations}
        />
      </div>
    </Fragment>
  );
};

export default FrqEditor;
